import {alpha} from '@mui/material/styles';

declare module '@mui/material/Paper' {
    interface PaperPropsVariantOverrides {
        highlighted: true;
    }
}

declare module '@mui/material/styles' {
    interface ColorRange {
        50: string;
        100: string;
        200: string;
        300: string;
        400: string;
        500: string;
        600: string;
        700: string;
        800: string;
        900: string;
    }

    // eslint-disable-next-line @typescript-eslint/no-empty-interface
    interface PaletteColor extends ColorRange {
    }

    interface Palette {
        baseShadow: string;
        /**
         * neutral — отдельный palette-канал для нейтральных интерактивных элементов.
         * Используется для outlined-кнопок вторичных действий ("Отмена", "Назад",
         * "Фильтры"), text-кнопок третичных действий и любых UI-элементов,
         * которые не должны конкурировать визуально с primary CTA.
         *
         * Отличие от secondary: secondary в этой теме оставлен для совместимости
         * с MUI internals (Switch, Slider, default Tab indicator), но в коде
         * приложения НЕ ИСПОЛЬЗУЕТСЯ. Используй neutral.
         *
         * Тип — SimplePaletteColorOptions (без ColorRange 50..900). Нам не нужны
         * шкальные значения у neutral — мы используем только main/light/dark/
         * contrastText. Если когда-нибудь понадобятся neutral[300] и т.д., тип
         * нужно будет расширить и заодно заполнить значения в обеих темах.
         */
        neutral: {
            light: string;
            main: string;
            dark: string;
            contrastText: string;
        };
    }

    interface PaletteOptions {
        neutral?: {
            light: string;
            main: string;
            dark: string;
            contrastText: string;
        };
    }

    interface TypeText {
        warning?: string;
    }
}

// Подключаем neutral к Button MUI: <Button color="neutral"> станет валидным
// для TypeScript. Runtime: MUI прочтёт theme.palette.neutral и применит цвета.
declare module '@mui/material/Button' {
    interface ButtonPropsColorOverrides {
        neutral: true;
    }
}

// IconButton тоже принимает color через тот же overrides-канал.
declare module '@mui/material/IconButton' {
    interface IconButtonPropsColorOverrides {
        neutral: true;
    }
}

/**
 * ============================================================================
 * Цветовая концепция Parallax
 * ============================================================================
 *
 * Эстетика: «тихий профессиональный инструмент». Длинные сессии в плотном
 * UI требуют приглушённой палитры — кислотные тона на 8 часов работы
 * утомляют. При этом цвета должны иметь характер, не быть generic
 * material-blue + material-green.
 *
 * Принципы шкалы:
 * - gray: нейтральная с лёгким холодным подтоном (saturation 8-12%, не 20-35%).
 *   На холодных мониторах в офисе это считывается как «техничность».
 * - brand: sapphire-indigo (hue 225), не голубой и не фиолетовый.
 *   Узнаваемый, но не «генеричный Material Blue».
 * - semantic: tailwind-like приглушённые тона. Success — emerald с teal-уклоном,
 *   warning — янтарный amber (а не оранжевый), error — теплый crimson
 *   (а не пожарный red).
 *
 * Принципы пары light/dark:
 * - Контраст текста к фону: ~14:1 для primary, ~5:1 для secondary в обеих темах.
 *   Не больше: 19:1 (как было: pure white на near-black) физически утомляет.
 * - Светлая тема: бумажный off-white default + чистый white paper.
 *   Свет приходит сверху → приподнятая поверхность ярче.
 * - Тёмная тема: глубокий графит, не near-black. Разница default/paper ~3%,
 *   достаточно чтобы paper «всплывал», без необходимости рамок.
 * ============================================================================
 */

/**
 * brand — sapphire-indigo. Узнаваемый, не голубой Material.
 * 400 (main в light) — основной CTA, насыщенный.
 * 500 (main в dark) — чуть светлее, чтобы оставаться читаемым на тёмном фоне.
 */
export const brand = {
    50: 'hsl(225, 100%, 97%)',
    100: 'hsl(225, 95%, 93%)',
    200: 'hsl(225, 90%, 85%)',
    300: 'hsl(225, 85%, 73%)',
    400: 'hsl(225, 75%, 62%)',  // primary.main (light)
    500: 'hsl(225, 78%, 68%)',  // primary.main (dark)
    600: 'hsl(225, 70%, 55%)',
    700: 'hsl(225, 68%, 45%)',  // primary.dark
    800: 'hsl(225, 65%, 32%)',
    900: 'hsl(225, 60%, 20%)',
};

/**
 * gray — лёгкий холодный подтон (saturation 10%, не 30%).
 * Близко к Vercel / Linear / Notion.
 */
export const gray = {
    50: 'hsl(220, 14%, 98%)',
    100: 'hsl(220, 13%, 95%)',
    200: 'hsl(220, 13%, 88%)',
    300: 'hsl(220, 12%, 78%)',
    400: 'hsl(220, 10%, 60%)',
    500: 'hsl(220, 10%, 45%)',
    600: 'hsl(220, 11%, 35%)',
    700: 'hsl(220, 12%, 22%)',
    800: 'hsl(220, 13%, 13%)',
    900: 'hsl(220, 13%, 9%)',
};

/**
 * green — emerald с teal-уклоном (hue 158, не 120).
 * 120 (чистый green) выглядит салатовым и кислотным.
 * 158 — естественный успех, как у Stripe / Vercel.
 */
export const green = {
    50: 'hsl(155, 65%, 96%)',
    100: 'hsl(155, 60%, 90%)',
    200: 'hsl(155, 55%, 78%)',
    300: 'hsl(158, 50%, 60%)',
    400: 'hsl(158, 55%, 48%)',  // success (dark theme)
    500: 'hsl(160, 65%, 36%)',  // success.main (light)
    600: 'hsl(160, 72%, 28%)',
    700: 'hsl(162, 78%, 22%)',
    800: 'hsl(162, 80%, 16%)',
    900: 'hsl(162, 82%, 10%)',
};

/**
 * orange — amber/янтарный (hue 38, не 45).
 * Чуть теплее, без зелёного оттенка.
 */
export const orange = {
    50: 'hsl(40, 95%, 96%)',
    100: 'hsl(40, 90%, 88%)',
    200: 'hsl(38, 90%, 75%)',
    300: 'hsl(38, 92%, 60%)',
    400: 'hsl(38, 92%, 50%)',  // warning (dark theme)
    500: 'hsl(36, 90%, 42%)',  // warning.main (light)
    600: 'hsl(34, 88%, 35%)',
    700: 'hsl(32, 88%, 28%)',
    800: 'hsl(30, 88%, 20%)',
    900: 'hsl(28, 88%, 14%)',
};

/**
 * red — crimson/тёплый, hue 355 (не чистый 0).
 * Чуть «человечнее» пожарного red.
 */
export const red = {
    50: 'hsl(355, 90%, 97%)',
    100: 'hsl(355, 85%, 92%)',
    200: 'hsl(355, 82%, 82%)',
    300: 'hsl(355, 78%, 70%)',
    400: 'hsl(355, 75%, 60%)',  // error (dark theme)
    500: 'hsl(355, 75%, 50%)',  // error.main (light)
    600: 'hsl(355, 75%, 42%)',
    700: 'hsl(355, 78%, 34%)',
    800: 'hsl(355, 80%, 25%)',
    900: 'hsl(355, 82%, 16%)',
};

// =============================================================================
// Light theme palette
// =============================================================================
const lightPalette = {
    mode: 'light' as const,

    common: {
        white: '#FFFFFF',
        black: '#000000',
    },

    primary: {
        light: brand[300],
        main: brand[400],
        dark: brand[700],
        contrastText: '#FFFFFF',
    },

    /**
     * secondary — оставлен для совместимости с MUI internals (Switch, Slider).
     * В коде приложения НЕ ИСПОЛЬЗОВАТЬ — вместо него использовать `neutral`.
     */
    secondary: {
        light: gray[400],
        main: gray[600],
        dark: gray[800],
        contrastText: '#FFFFFF',
    },

    /**
     * neutral — для outlined-кнопок вторичных действий ("Отмена", "Назад",
     * "Фильтры") и text-кнопок третичных действий.
     *
     * Цвета подобраны так, чтобы:
     *   - main давал хороший контраст с фоном default (для текста и border'а)
     *     не конкурируя с primary визуально (не яркий, серый)
     *   - light → для hover/focus подсветок
     *   - dark → для активного состояния
     *   - contrastText → для редкого contained="neutral" (заливка серым)
     *
     * В light: main = gray[700] (тёмный графит) — читается как "обычный текст".
     * Это правильный цвет для outlined-кнопки "Отмена" — она читается как кнопка,
     * но не отвлекает внимание от primary "Сохранить".
     */
    neutral: {
        light: gray[300],
        main: gray[700],
        dark: gray[800],
        contrastText: '#FFFFFF',
    },

    info: {
        light: brand[200],
        main: brand[400],
        dark: brand[700],
        contrastText: '#FFFFFF',
    },

    success: {
        light: green[300],
        main: green[500],
        dark: green[700],
        contrastText: '#FFFFFF',
    },

    warning: {
        light: orange[300],
        main: orange[500],
        dark: orange[700],
        contrastText: '#FFFFFF',
    },

    error: {
        light: red[300],
        main: red[500],
        dark: red[700],
        contrastText: '#FFFFFF',
    },

    /**
     * inputWarning — кастомный канал для предупреждения именно в формах
     * (мягче и темнее обычного warning, чтобы не «кричать» в плотном UI).
     */
    inputWarning: {
        light: orange[400],
        main: orange[600],
        dark: orange[800],
        contrastText: '#FFFFFF',
    },

    grey: {...gray},

    /**
     * Divider в light — серый 13%, без alpha. Должен быть видимым.
     * alpha(gray[300], 0.4) (как было) практически невидимо на off-white.
     */
    divider: 'hsl(220, 13%, 90%)',

    background: {
        /**
         * default — тёплый off-white (hue 40), легче для глаз при долгих сессиях
         * чем pure white. Бумажный, не «больничный».
         */
        default: 'hsl(40, 14%, 98%)',
        /**
         * paper — чистый white. На светлой теме приподнятая поверхность
         * ярче default (свет сверху).
         */
        paper: '#FFFFFF',
    },

    text: {
        primary: gray[800],     // hsl(220, 13%, 13%) — глубокий, но не чёрный
        secondary: gray[500],   // hsl(220, 10%, 45%) — реально читаемый
        disabled: gray[300],    // hsl(220, 12%, 78%)
        warning: orange[600],
    },

    action: {
        active: gray[600],
        hover: alpha(gray[800], 0.04),       // ~4% тёмная подсветка
        selected: alpha(gray[800], 0.08),    // 2x от hover, чётко видно
        disabled: alpha(gray[800], 0.26),
        disabledBackground: alpha(gray[800], 0.06),
        focus: alpha(brand[400], 0.20),
        hoverOpacity: 0.04,
        selectedOpacity: 0.08,
        disabledOpacity: 0.38,
        focusOpacity: 0.12,
        activatedOpacity: 0.12,
    },

    baseShadow:
        '0 1px 2px hsla(220, 13%, 13%, 0.04), 0 4px 12px hsla(220, 13%, 13%, 0.06)',
};

// =============================================================================
// Dark theme palette
// =============================================================================
const darkPalette = {
    mode: 'dark' as const,

    common: {
        white: '#FFFFFF',
        black: '#000000',
    },

    primary: {
        light: brand[300],
        main: brand[500],       // на тёмном фоне берём чуть светлее
        dark: brand[700],
        contrastText: '#FFFFFF',
    },

    /**
     * secondary — оставлен для совместимости с MUI internals (Switch, Slider).
     * В коде приложения НЕ ИСПОЛЬЗОВАТЬ — вместо него использовать `neutral`.
     */
    secondary: {
        light: gray[300],
        main: gray[400],
        dark: gray[600],
        contrastText: gray[900],
    },

    /**
     * neutral в dark: main светлее, чем в light, чтобы быть видимым на тёмном
     * фоне. gray[300] даёт ~6:1 контраст к default — комфортно для границ
     * outlined-кнопок и для текста text-кнопок.
     */
    neutral: {
        light: gray[400],
        main: gray[300],
        dark: gray[100],
        contrastText: gray[900],
    },

    info: {
        light: brand[300],
        main: brand[500],
        dark: brand[800],
        contrastText: '#FFFFFF',
    },

    success: {
        light: green[300],
        main: green[400],       // emerald, видимый на тёмном
        dark: green[700],
        contrastText: gray[900],
    },

    warning: {
        light: orange[300],
        main: orange[400],
        dark: orange[700],
        contrastText: gray[900],
    },

    error: {
        light: red[300],
        main: red[400],
        dark: red[700],
        contrastText: '#FFFFFF',
    },

    inputWarning: {
        light: orange[300],
        main: orange[500],
        dark: orange[700],
        contrastText: gray[900],
    },

    grey: {...gray},

    /**
     * Divider в dark — слегка осветлённый paper. Видимый, но мягкий.
     */
    divider: 'hsl(220, 13%, 22%)',

    background: {
        /**
         * default — глубокий графит, не near-black.
         * gray[900] был hsl(220, 35%, 3%) — почти чёрный, 19:1 к белому — утомительно.
         * Новое значение даёт ~15:1 — достаточно, но не максимум.
         */
        default: gray[900],                   // hsl(220, 13%, 9%)
        /**
         * paper — заметно светлее default (+~3%), читается как «приподнятая
         * поверхность» без рамок и теней.
         */
        paper: 'hsl(220, 13%, 13%)',
    },

    text: {
        /**
         * primary — почти чистый, но не 100% белый.
         * Pure white на тёмном фоне даёт перенасыщенный контраст.
         */
        primary: 'hsl(0, 0%, 98%)',
        /**
         * secondary — нейтральный cool-gray, не голубой #A9B7C6 (как было —
         * слишком много синевы, добавляло «залитость» интерфейса).
         */
        secondary: 'hsl(220, 10%, 68%)',
        disabled: 'hsl(220, 10%, 40%)',
        warning: orange[300],
    },

    action: {
        active: 'hsl(220, 10%, 68%)',
        /**
         * hover в dark — alpha от white, не от gray.
         * alpha(gray[600], 0.2) на тёмном фоне даёт прирост яркости ~3% —
         * ниже порога восприятия. alpha(white, 0.06) — видимая подсветка.
         */
        hover: alpha('#FFFFFF', 0.06),
        selected: alpha('#FFFFFF', 0.10),
        disabled: alpha('#FFFFFF', 0.30),
        disabledBackground: alpha('#FFFFFF', 0.08),
        focus: alpha(brand[400], 0.25),
        hoverOpacity: 0.06,
        selectedOpacity: 0.10,
        disabledOpacity: 0.38,
        focusOpacity: 0.12,
        activatedOpacity: 0.16,
    },

    baseShadow:
        '0 2px 8px hsla(0, 0%, 0%, 0.4), 0 8px 24px hsla(0, 0%, 0%, 0.5)',
};

export const colorSchemes = {
    light: {palette: lightPalette},
    dark: {palette: darkPalette},
};